import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import { useAuth } from '../contexts/AuthContext';
import { playSound } from '../utils/notificationSound';
import { useNavigate } from 'react-router-dom';
import { AppLanguage, isAppLanguage } from '../i18n/types';
import { LOGIN_LANGUAGE_OPTIONS, translateLogin } from '../i18n/login';
import './Login.css';

const LOGIN_LANG_STORAGE_KEY = 'loginLanguage';

const LoginFlag: React.FC<{ option: (typeof LOGIN_LANGUAGE_OPTIONS)[number] }> = ({ option }) => {
  const [imgFailed, setImgFailed] = useState(!option.flag);
  if (!imgFailed && option.flag) {
    return (
      <img
        src={option.flag}
        alt=""
        className="login-flag-icon"
        onError={() => setImgFailed(true)}
      />
    );
  }
  return <span className="login-flag-emoji" aria-hidden>{option.emoji}</span>;
};

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<AppLanguage>(() => {
    const saved = localStorage.getItem(LOGIN_LANG_STORAGE_KEY);
    return isAppLanguage(saved) ? saved : 'sr';
  });
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(LOGIN_LANG_STORAGE_KEY, language);
  }, [language]);

  const t = (key: string) => translateLogin(language, key);
  const currentLang = LOGIN_LANGUAGE_OPTIONS.find((o) => o.code === language) || LOGIN_LANGUAGE_OPTIONS[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(username, password);
      playSound('loginSuccess');
      navigate('/');
    } catch (err: any) {
      setError(err.message || t('login.error'));
      playSound('loginFailed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-container">
      <div className="login-card">
        <div className="login-left-banner">
          <img
            src="/login-banner.jpg"
            alt=""
            className="login-banner-image"
            loading="eager"
          />
          <div className="login-banner-overlay">
            <h2 className="login-banner-title">Thermowool</h2>
          </div>
        </div>

        <div className="login-right-form">
          <div className="login-form-header">
            <div className="login-form-header-text">
              <h1 className="login-welcome-title">{t('login.welcome')}</h1>
              <p className="login-welcome-subtitle">{t('login.subtitle')}</p>
            </div>

            <div className="login-language-row">
              <label className="login-language-label" htmlFor="login-language">
                {t('login.language')}
              </label>
              <Dropdown
                inputId="login-language"
                value={language}
                options={LOGIN_LANGUAGE_OPTIONS}
                optionLabel="label"
                optionValue="code"
                onChange={(e) => setLanguage(e.value as AppLanguage)}
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
                className="login-language-dropdown"
                panelClassName="login-language-dropdown-panel"
                valueTemplate={() => (
                  <div className="login-language-value">
                    <LoginFlag option={currentLang} />
                    <span className="login-language-value-text">{currentLang.label}</span>
                  </div>
                )}
                itemTemplate={(option: (typeof LOGIN_LANGUAGE_OPTIONS)[number]) => (
                  <div className="login-language-item">
                    <LoginFlag option={option} />
                    <span>{option.label}</span>
                  </div>
                )}
              />
            </div>
          </div>

          {error && (
            <Message severity="error" text={error} className="login-error-message" />
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="username">{t('login.username')}</label>
              <InputText
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('login.usernamePlaceholder')}
                className="login-input"
                autoComplete="username"
                required
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">{t('login.password')}</label>
              <Password
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                className="login-input"
                style={{ width: '100%' }}
                feedback={false}
                toggleMask
                required
              />
            </div>

            <Button
              type="submit"
              label={t('login.submit')}
              loading={loading}
              className="login-submit-button"
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
